import { Grid } from '@material-ui/core';
import { Button, Dialog, Icons, Link, Tooltip, Typography } from '@nutrien/cxp-components';
import localforage from 'localforage';
import React, { useEffect, useState } from 'react';
import type { DropResult } from 'react-beautiful-dnd';
import { DragDropContext } from 'react-beautiful-dnd';
import { Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { useMst } from '../../mobx-models/Root';
import {
  AimsFile,
  Deficiency,
  DeficiencyComponentRemovalPayload,
  DeficiencyFileEdited,
  DeficiencyListModified,
  Discipline,
  FileOperation,
  GroupedBasedOnShowOnReport,
  GroupedBasedOnShowOnReportForUploading,
} from '../../models/models';
import type { HeirachyResult } from '../../rxdb/assetsByAreaHeirachy/assetsByAreaHierarchy.types';
import useAssetsByAreaHierarchy from '../../rxdb/assetsByAreaHeirachy/useAssetsByAreaHierarchy';
import type { ComponentRemovalCategories } from '../../rxdb/componentRemovalCategories/componentRemovalCategories.types';
import useComponentRemovalCategories from '../../rxdb/componentRemovalCategories/useComponentRemovalCategories';
import useComponentType from '../../rxdb/componentType/useComponentType';
import useConditionRating from '../../rxdb/conditionRating/useConditionRating';
import type { DeficiencyActionedList } from '../../rxdb/deficiencyActionedList/deficiencyActionedList.types';
import useDeficiencyComponentRemoval from '../../rxdb/deficiencyComponentRemoval/useDeficiencyComponentRemoval';
import type { DeficiencyFilesDocument } from '../../rxdb/deficiencyFiles/deficiencyFiles.types';
import { useDeficiencyFiles } from '../../rxdb/deficiencyFiles/useDeficiencyFiles';
import type { ComponentAreaInfo } from '../../rxdb/deficiencyList/deficiencyList.types';
import { useDeficiencyList } from '../../rxdb/deficiencyList/useDeficiencyList';
import type { DeficiencyVersionFiles } from '../../rxdb/deficiencyVersionFiles/deficiencyVersionFiles.types';
import { useDeficiencyVersionFiles } from '../../rxdb/deficiencyVersionFiles/useDeficiencyVersionFiles';
import useElevationRange from '../../rxdb/elevationRange/useElevationRange';
import useEnvironmentalCondition from '../../rxdb/environmentalCondition/useEnvironmentalCondition';
import type {
  InspectionListDocument,
  MobileComponentLocationHeirarchy,
} from '../../rxdb/inspectionList/inspectionList.types';
import { useInspectionList } from '../../rxdb/inspectionList/useInspectionList';
import useOperationalDowntime from '../../rxdb/operationalDowntime/useOperationalDowntime';
import usePrimaryCauseOfFailure from '../../rxdb/primaryCauseOfFailure/usePrimaryCauseOfFailure';
import useRemedialAction from '../../rxdb/remedialAction/useRemedialAction';
import useRepairFactor from '../../rxdb/repairFactor/useRepairFactor';
import type { TaskList } from '../../rxdb/taskList/taskList.types';
import { useTaskList } from '../../rxdb/taskList/useTaskList';
import useTrafficSafetyScore from '../../rxdb/trafficSafetyScore/useTrafficSafetyScore';
import useUrgency from '../../rxdb/urgency/useUrgency';
import { INVALID_FILE, SPECIAL_CHARACTER_ERROR } from '../../utilities/constants';
import {
  AreaTypes,
  ComponentInfo,
  ComponentRemovalCategories as ComponentRemovalCategoriesId,
  InspectionStatus,
  TaskStatus,
} from '../../utilities/enums';
import { useFileStorage } from '../../utilities/useFileStorage';
import { useNotification } from '../../utilities/useNotification';
import {
  autoVersionIfFileExists,
  checkFileCorruption,
  groupObjectArrayByKey,
  hasSpecialCharacter,
  isNull,
} from '../../utilities/utilityFunctions';
import AuditLogs from '../AuditLogs';
import ComponentInfoPanel from '../ComponentInfo';
import ComponentInfoPanelForElectrical from '../ComponentInfoForElectrical';
import ComponentRemovalPanel from '../ComponentRemovalPanel';
import DeficiencyActionPanel from '../DeficiencyAction/DeficiencyAction';
import DeficiencyComments from '../DeficiencyComments';
import DeficiencyFilesView from '../DeficiencyFilesView';
import CancelConfirmation from '../DiscardDraft';
import FileSelectionView from '../FileSelectionView';
import GenericSidePanel from '../GenericSidePanel';
import RiskAssessmentPanel from '../RiskAssessment';
import RiskAssessmentPanelForElectrical from '../RiskAssessmentForElectrical';
import useStyles from './EditDeficiency.style';
interface Props {
  open: boolean;
  addFlag: boolean;
  onClose: () => void;
  editFile: Deficiency;
  taskData: TaskList;
  isEditable: boolean;
  isActiveDeficiency: boolean;
}

const editedBlobFileStore = localforage.createInstance({
  name: 'edited-blob-file-store',
});

const EditDeficiency: React.FC<Props> = ({
  open,
  addFlag,
  onClose,
  editFile,
  taskData,
  isEditable,
  isActiveDeficiency,
}) => {
  const classes = useStyles();
  const isDeficiencyEditable = isEditable;
  const { SiteDiscipline, CurrentUserSelected } = useMst();
  const { errorNotification } = useNotification();
  const { componentTypeListData } = useComponentType(SiteDiscipline.disciplineId);
  const { remedialActionListData } = useRemedialAction(SiteDiscipline.disciplineId);
  const { primaryCauseOfFailureListData } = usePrimaryCauseOfFailure(SiteDiscipline.disciplineId);
  const { conditionRatingListData } = useConditionRating(SiteDiscipline.disciplineId);
  const { urgencyListData } = useUrgency(SiteDiscipline.disciplineId);
  const { operationalDowntimeListData } = useOperationalDowntime();
  const { repairFactorListData } = useRepairFactor();
  const { trafficSafetyScoreListData } = useTrafficSafetyScore();
  const { environmentalConditionListData } = useEnvironmentalCondition();
  const { elevationRange } = useElevationRange();
  const { assetsByAreaHierarchyData } = useAssetsByAreaHierarchy();
  const { inspectionListInitialized, getInspectionById, inspectionListCollection } =
    useInspectionList();
  const { setTaskListInfo } = useTaskList();
  const { uploadDeficiencyFile, deleteFileFromAttachmentStore } = useFileStorage();
  const { saveDeficiencyList, updateDeficiencyComment, updateActiveDeficiencyComments } =
    useDeficiencyList();
  const { updateDeficiencyFile, deleteDeficiencyFile } = useDeficiencyFiles();
  const { updateActiveDeficiencyFile, deleteActiveDeficiencyFile } = useDeficiencyVersionFiles();
  const { componentRemovalCategories } = useComponentRemovalCategories();
  const { saveDeficiencyComponentRemoval } = useDeficiencyComponentRemoval(
    CurrentUserSelected.assetId,
  );
  const isElectrical = SiteDiscipline.disciplineId === Discipline.Electrical;
  const isShaft = SiteDiscipline.disciplineId === Discipline.Shaft;
  const currentUser = CurrentUserSelected?.currentUser;

  const [deficiencyPayload, setDeficiencyPayload] = useState<DeficiencyListModified>(
    {} as DeficiencyListModified,
  );
  const [deficiencyActionedList, setDeficiencyActionedList] = useState<DeficiencyActionedList>(
    {} as DeficiencyActionedList,
  );
  const [deficiencyComponentRemovalPayload, setDeficiencyComponentRemovalPayload] =
    useState<DeficiencyComponentRemovalPayload>({} as DeficiencyComponentRemovalPayload);
  const [deficiencyFiles, setDeficiencyFiles] = useState<
    (DeficiencyFileEdited | DeficiencyFilesDocument)[]
  >([]);
  const [filesToUpload, setFilesToUpload] = useState<AimsFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<AimsFile[]>([]);
  const [openFileUploadView, setOpenFileUploadView] = useState<boolean>(false);
  const [treeData, setTreeData] = useState<HeirachyResult[]>([]);
  const [areasList, setAreasList] = useState<HeirachyResult[]>([]);
  const [showOnReportImages, setShowOnReportImages] = useState<GroupedBasedOnShowOnReport>({
    true: [],
    false: [],
  });
  const [showOnReportImages1, setShowOnReportImages1] =
    useState<GroupedBasedOnShowOnReportForUploading>({
      true: [],
      false: [],
    });
  const [isChange, setIsChange] = useState<boolean>(false);
  const [inspection, setInspection] = useState<InspectionListDocument | null>(null);
  const [trafficSafetyId, setTrafficSafetyId] = useState<number>(0);
  const [valid, setValid] = useState<boolean>(true);
  const [updating, setUpdating] = useState(false);
  const [openSidePanel, setOpenSidePanel] = useState(false);
  const [openForAlertInfo, setOpenForAlertInfo] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [componentAreaLength, setComponentAreaLength] = useState<number>(0);
  const [maxEnvironmentalCondition, setMaxEnvironmentalCondition] = useState(0);
  const [openCancelConfirmation, setOpenCancelConfirmation] = useState<boolean>(false);
  const [validCommment, setValidComment] = useState<boolean>(false);
  const [openAuditLogsDialog, setOpenAuditLogsDialog] = useState<boolean>(false);
  const [showToolTip, setShowToolTip] = useState<boolean>(false);
  const [componentRemovalListData, setComponentRemovalListData] = useState<
    ComponentRemovalCategories[]
  >([]);
  const componentVersionInspectionId = isActiveDeficiency
    ? deficiencyPayload.componentVersionInspectionId === CurrentUserSelected.inspectionId
      ? deficiencyPayload.componentVersionInspectionId
      : CurrentUserSelected.inspectionId
    : null;
  const componentVersionId = isActiveDeficiency
    ? componentVersionInspectionId === null
      ? null
      : deficiencyPayload.componentVersionId
    : null;
  const filesNotAvailableToolTip = `Please connect to network to view Deficiency Photos.`;

  const wasCreatedInPreviousInspection =
    deficiencyPayload.componentVersionInspectionId &&
    deficiencyPayload.componentVersionInspectionId !== CurrentUserSelected.inspectionId;

  const getInspectionListDataFromRxdb = async () => {
    const inspectionListDocument = await getInspectionById(
      String(CurrentUserSelected.inspectionId),
    );
    if (inspectionListDocument) setInspection(inspectionListDocument);
  };

  const reorder = (list: DeficiencyFileEdited[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const showInfoMessage = (value: boolean) => {
    setShowToolTip(value);
  };

  const handleTooltipCloseForAlertInfo = (e: {
    stopPropagation: () => void;
    preventDefault: () => void;
  }) => {
    setOpenForAlertInfo(false);
    e.preventDefault();
    e.stopPropagation();
  };

  const handleTooltipOpenForAlertInfo = (e: { stopPropagation: () => void }) => {
    setOpenForAlertInfo(true);
    e.stopPropagation();
  };

  useEffect(() => {
    const clone = { ...deficiencyComponentRemovalPayload };
    if (clone?.componentRemovalId !== null || clone?.componentRemovalIdBySC !== null)
      setComponentRemovalListData(componentRemovalCategories);
    else {
      setComponentRemovalListData(
        componentRemovalCategories.filter(
          category => category?.pkComponentRemovalCategory !== ComponentRemovalCategoriesId.NONE,
        ),
      );
    }
  }, [componentRemovalCategories, deficiencyComponentRemovalPayload]);

  const move = (
    newlist: GroupedBasedOnShowOnReport,
    fileUuid: string,
    sInd: string,
    dInd: string,
    flag: string,
  ) => {
    let destList: DeficiencyFileEdited[] = [];
    const list: GroupedBasedOnShowOnReport = newlist;
    if ((sInd === 'true' || sInd === 'false') && (dInd === 'true' || dInd === 'false')) {
      if (list.hasOwnProperty('false') === false && dInd === 'false' && list[dInd] === undefined) {
        list.false = [];
        destList = list.false;
      }
      if (list.hasOwnProperty('true') === false && dInd === 'true' && list[dInd] === undefined) {
        list.true = [];
        destList = list.true;
      }

      if (list[dInd] !== undefined) {
        destList = list[dInd];
      }
      const sourceList = list[sInd];
      if (dInd === 'true' && destList !== undefined && destList?.length >= 5) {
        return list;
      }
      const fileIndex = sourceList.findIndex(item =>
        flag === 'fileId'
          ? (item as DeficiencyFileEdited).fileAimsUUID === fileUuid
          : `${(item as AimsFile)?.updatedName}-${(item as AimsFile)?.lastModified}` === fileUuid,
      );
      const sourceItem = sourceList[fileIndex];
      sourceItem.showOnReport = !sourceItem.showOnReport;
      destList.push(sourceItem);
      sourceList.splice(sourceList.indexOf(sourceItem), 1);
    }
    return list;
  };

  const getTreeData = (
    heirachyData: HeirachyResult[],
    defaultData: MobileComponentLocationHeirarchy[],
  ) => {
    for (let i = 0; i < heirachyData.length; i += 1) {
      const obj = defaultData.find(
        (item: { id: string }) => item.id === String(heirachyData[i].id),
      );
      if (
        obj &&
        heirachyData[i]?.type !== AreaTypes.SURFACE_ASSET &&
        heirachyData[i]?.type !== AreaTypes.UNDERGROUND_ASSET &&
        heirachyData[i]?.type !== AreaTypes.SHAFT_ASSET
      ) {
        setTreeData(currData => [...currData, heirachyData[i]]);
      }
      if (heirachyData[i].children?.length > 0) {
        getTreeData(heirachyData[i].children, defaultData);
      }
    }
  };

  const handleImageUploads = async (
    fileList: AimsFile[],
    deficiencyId: string,
    deficiencyUuid: string,
  ) => {
    const files = [...fileList];
    const existingFilesGrouped = groupObjectArrayByKey(deficiencyFiles, 'showOnReport');
    const GroupuploadFiles = groupObjectArrayByKey(files, 'showOnReport');

    if (GroupuploadFiles) {
      GroupuploadFiles.false = GroupuploadFiles.false ? GroupuploadFiles.false : [];
    }
    if (GroupuploadFiles.true !== undefined && GroupuploadFiles.true.length > 5) {
      GroupuploadFiles.false = GroupuploadFiles.false.concat(GroupuploadFiles.true.slice(5));
      GroupuploadFiles.false.filter(
        (item: AimsFile, index: number) =>
          (GroupuploadFiles[index].showOnReport =
            item.showOnReport === true ? false : item.showOnReport),
      );
      GroupuploadFiles.true = GroupuploadFiles.true.slice(0, 5);
    }

    await Promise.all(
      Object.keys(GroupuploadFiles).map(key => {
        GroupuploadFiles[key].map((fileToUpload: AimsFile, index: number) => {
          GroupuploadFiles[key][index].fileSortOrder =
            existingFilesGrouped !== undefined && existingFilesGrouped[key]?.length > 0
              ? existingFilesGrouped[key].length
              : index;
          uploadDeficiencyFile(
            fileToUpload,
            deficiencyId,
            deficiencyUuid,
            isActiveDeficiency,
            componentVersionInspectionId,
            componentVersionId,
            editFile?.isAdhoc,
          );
        });
      }),
    ).catch(error => {
      throw new Error('handleImageUploads error', error);
    });
  };

  const checkIfOtherSelected = () => {
    let checkValid = false;

    if (
      deficiencyPayload[ComponentInfo.REMEDIAL_ACTION]?.toLowerCase() === 'other' &&
      !deficiencyPayload[ComponentInfo.COMMENT_REMEDIAL_ACTION]?.length
    ) {
      checkValid = true;
    }

    if (
      deficiencyPayload[ComponentInfo.COMPONENT_TYPE]?.toLowerCase() === 'other' &&
      !deficiencyPayload[ComponentInfo.COMMENT_ASSET_COMPONENT]?.length
    ) {
      checkValid = true;
    }

    if (
      deficiencyPayload[ComponentInfo.CAUSE_OF_FAILURE]?.toLowerCase() === 'other' &&
      !deficiencyPayload[ComponentInfo.COMMENT_OBSERVATION]?.length
    ) {
      checkValid = true;
    }

    return checkValid;
  };

  const checkComponentTypeForShaft = () => {
    if (isShaft) return deficiencyPayload[ComponentInfo.FK_COMPONENT];
    return deficiencyPayload[ComponentInfo.ASSET_CATEGORY_COMPONENT_ID];
  };

  const validateInputs = () => {
    if (
      !deficiencyPayload[ComponentInfo.NAME] ||
      deficiencyPayload[ComponentInfo.NAME]?.trim() === '' ||
      !checkComponentTypeForShaft() ||
      !deficiencyPayload[ComponentInfo.REMEDIAL_ACTION_ID] ||
      !deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]?.length ||
      !deficiencyPayload[ComponentInfo.OPERTION_DOWNTIME] ||
      !deficiencyPayload[ComponentInfo.CONDITION_RATING] ||
      !deficiencyPayload[ComponentInfo.URGENCY] ||
      !(trafficSafetyId > 0)
    ) {
      setValid(true);
    } else {
      if (checkIfOtherSelected()) {
        setValid(true);
      } else {
        setValid(false);
      }
    }
  };

  useEffect(() => {
    if (!isElectrical) {
      if (trafficSafetyId && (deficiencyPayload.isChanged || isChange)) validateInputs();
      else setValid(true);
    }
  }, [trafficSafetyId]);

  const validateComments = () => {
    if (
      deficiencyPayload.commentInspectorField !==
        (editFile.commentInspectorField ? editFile.commentInspectorField : '') ||
      deficiencyPayload.attentionFlag !== editFile?.attentionFlag
    ) {
      setValid(false);
    } else {
      setValid(true);
    }
  };

  const validateInputForElectrical = () => {
    if (
      !deficiencyPayload[ComponentInfo.NAME] ||
      deficiencyPayload[ComponentInfo.NAME]?.trim() === '' ||
      !deficiencyPayload[ComponentInfo.REMEDIAL_ACTION_ID] ||
      !deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]?.length ||
      !deficiencyPayload[ComponentInfo.REPAIR_FACTOR_ID] ||
      !deficiencyPayload[ComponentInfo.COMPONENT_TYPE] ||
      !(maxEnvironmentalCondition > 0)
    ) {
      setValid(true);
    } else {
      setValid(false);
    }
  };

  const isTaskStatusFIorLISign = () => {
    if (
      taskData.fkInspectionTaskStatusCategory === TaskStatus.FI_SIGN_PENDING ||
      ((deficiencyPayload.isReInspectedComponent
        ? true
        : taskData.fkInspectorField === taskData.fkInspectorLead) &&
        (taskData.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING ||
          taskData.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL))
    ) {
      return false;
    }
    return true;
  };

  const updateDeficiencyFiles = async () => {
    const deficiencyFilesEdited = deficiencyFiles as DeficiencyFileEdited[];
    deficiencyFilesEdited.map(async (deficiencyFile: DeficiencyFileEdited) => {
      if (deficiencyFile?.operation === FileOperation.fileDeleted) {
        if (isActiveDeficiency) {
          const deficiencyDeletePayload: DeficiencyVersionFiles = {
            ...deficiencyFile,
            componentVersionInspectionId,
            componentVersionId,
          };
          await deleteActiveDeficiencyFile(deficiencyDeletePayload);
          await deleteFileFromAttachmentStore(deficiencyFile.fileComponentVersionUUId);
        } else {
          await deleteDeficiencyFile(deficiencyFile.fileAimsUUID, 'true');
          await deleteFileFromAttachmentStore(deficiencyFile.fileAimsUUID);
        }
      } else if (deficiencyFile?.operation === FileOperation.fileDetailsEdited) {
        if (isActiveDeficiency) {
          const deficiencyUpdatePayload: DeficiencyVersionFiles = {
            ...deficiencyFile,
            componentVersionInspectionId,
            componentVersionId,
            isDelete: 'False',
            fileDescription: deficiencyFile.fileDescription || '',
          };
          await updateActiveDeficiencyFile(deficiencyUpdatePayload);
        } else {
          await updateDeficiencyFile(
            deficiencyFile.fileAimsUUID,
            deficiencyFile.fileName,
            deficiencyFile.showOnReport,
            deficiencyFile.fileDescription,
            'False',
            isActiveDeficiency,
            deficiencyFile.fileSortOrder,
          );
        }
      } else if (
        deficiencyFile?.operation === FileOperation.fileCopy ||
        deficiencyFile?.operation === FileOperation.fileBlobEdited
      ) {
        const fileData = (await editedBlobFileStore.getItem(
          deficiencyFile.fileAimsUUID,
        )) as AimsFile;
        await uploadDeficiencyFile(
          fileData,
          deficiencyFile?.deficiencyId,
          deficiencyFile?.deficiencyUUID,
          isActiveDeficiency,
          componentVersionInspectionId,
          componentVersionId,
          editFile?.isAdhoc,
          deficiencyFile,
        );
      }
    });
  };

  const handleComponentRemoval = async (componentVersionUUId: string) => {
    let cloneDeficiencyRemoval = { ...deficiencyComponentRemovalPayload };
    delete cloneDeficiencyRemoval?.parentIdSearch;
    cloneDeficiencyRemoval = {
      ...cloneDeficiencyRemoval,
      componentVersionId,
      componentVersionInspectionId,
      componentVersionUUId,
      deficiencyUUID: deficiencyPayload.pkDeficiencyUUID || uuidv4(),
      deficiencyId: deficiencyPayload.componentFailureId,
    };
    await saveDeficiencyComponentRemoval(cloneDeficiencyRemoval);
  };

  const handleSave = async () => {
    const componentVersionUUId = isActiveDeficiency
      ? deficiencyPayload?.componentVersionInspectionId === CurrentUserSelected.inspectionId
        ? deficiencyPayload.componentVersionUUID
        : uuidv4()
      : deficiencyPayload.componentVersionUUID ?? uuidv4();
    const isDeficiencyCommentChange =
      (deficiencyPayload?.commentInspectorField !== editFile?.commentInspectorField ||
        deficiencyPayload?.attentionFlag !== editFile?.attentionFlag) &&
      !deficiencyPayload.isChanged;
    setUpdating(true);
    if (
      (deficiencyPayload?.commentInspectorField !== editFile?.commentInspectorField ||
        deficiencyPayload?.attentionFlag !== editFile?.attentionFlag) &&
      !deficiencyPayload.isChanged
    ) {
      if (isActiveDeficiency) {
        const clone = {
          ...deficiencyPayload,
          componentVersionInspectionId,
          componentVersionId,
          componentVersionUUID: componentVersionUUId,
        };
        await updateActiveDeficiencyComments(clone);
        if (componentVersionUUId !== deficiencyComponentRemovalPayload.componentVersionUUId) {
          handleComponentRemoval(componentVersionUUId);
        }
      } else
        await updateDeficiencyComment(
          deficiencyPayload?.componentFailureId,
          deficiencyPayload?.commentInspectorField,
          deficiencyPayload?.attentionFlag,
        );
    }
    let deficiencyResult;
    if (!deficiencyPayload[ComponentInfo.DATE_OBSERVED]) {
      const newDate = new Date();
      deficiencyPayload[ComponentInfo.DATE_OBSERVED] = newDate.toISOString();
    }
    if (
      (deficiencyPayload.isChanged ||
        componentVersionUUId !== deficiencyPayload?.componentVersionUUID) &&
      !isDeficiencyCommentChange
    ) {
      let clone = { ...deficiencyPayload };
      if (deficiencyActionedList && Object.keys(deficiencyActionedList).length) {
        clone = {
          ...clone,
          isReInspectedComponent: !!deficiencyActionedList,
          componentVersionId,
          componentVersionInspectionId,
        };
        if (!deficiencyPayload.isActive) {
          clone = {
            ...clone,
            isActive: true,
            isChanged: true,
            isDelete: 'False',
          };
        }
      }
      clone = {
        ...clone,
        componentVersionUUID: componentVersionUUId,
      };
      deficiencyResult = await saveDeficiencyList(clone);
      if (isTaskStatusFIorLISign() || CurrentUserSelected.isAdhoc) {
        await setTaskListInfo(TaskStatus.IN_PROGRESS);
      }
    }

    await updateDeficiencyFiles();

    await handleImageUploads(
      filesToUpload,
      deficiencyPayload.componentFailureId,
      deficiencyResult
        ? isNull(deficiencyResult, ComponentInfo.PK_DEFICIENCY_UUID, '')
        : isNull(deficiencyPayload, ComponentInfo.PK_DEFICIENCY_UUID, ''),
    );

    if (
      isActiveDeficiency &&
      (deficiencyComponentRemovalPayload.isChanged ||
        (deficiencyPayload.isChanged && !deficiencyPayload.isActive))
    ) {
      handleComponentRemoval(componentVersionUUId);
    }

    setFilesToUpload([]);
    setUpdating(false);
    onClose();
  };

  const selectComponentLocations = () => {
    setOpenSidePanel(true);
  };

  const handleTreeDataSearch = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchText(e.target.value);
    if (e.target.value?.length > 3) {
      const location: HeirachyResult[] = JSON.parse(JSON.stringify(treeData));
      const filterArray = (itemObj: {
        name: { toString: () => string };
        children: HeirachyResult[];
      }): boolean | number => {
        const item = itemObj;
        return (
          String(item.name)?.toLowerCase().indexOf(e.target.value?.toLowerCase()) > -1 ||
          (item.children && (item.children = item.children.filter(filterArray))?.length)
        );
      };
      const filteredData = location.filter(filterArray);
      setAreasList(JSON.parse(JSON.stringify(filteredData)));
    } else {
      setAreasList(treeData);
    }
  };

  const findTrafficSafetyScore = () => {
    const result = deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]?.map(
      (item: { trafficSafety: number }) => {
        return item.trafficSafety;
      },
    );
    if (result) {
      setTrafficSafetyId(() => Math.max(...result) | 0);
    }
    setComponentAreaLength(deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]?.length);
    validateInputs();
  };

  const findEnvironmentalCondition = () => {
    const tempArray = deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]?.map(
      (item: ComponentAreaInfo) => {
        const index = environmentalConditionListData.findIndex(
          item2 => item2.areaId === String(item.areaId),
        );
        if (index !== -1) {
          setMaxEnvironmentalCondition(
            Math.max(environmentalConditionListData[index]?.riskValue, maxEnvironmentalCondition),
          );
          return environmentalConditionListData[index]?.riskValue;
        } else return -1;
      },
    );
    if (tempArray) {
      setMaxEnvironmentalCondition(Math.max(...tempArray));
    }
    setComponentAreaLength(deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]?.length);
    if (deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]?.length < 1) {
      setMaxEnvironmentalCondition(0);
    }
    validateInputForElectrical();
  };

  const updateDeficiencyToReinspected = () => {
    setDeficiencyPayload({
      ...deficiencyPayload,
      isReInspectedComponent: true,
    });
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }
    const draggableIdarray = result.draggableId.split('^');
    let defFilePayloadData =
      Object.keys(showOnReportImages).length !== 0
        ? JSON.parse(JSON.stringify(showOnReportImages))
        : showOnReportImages1;
    const sInd = source.droppableId;
    const dInd = destination.droppableId;
    const listOfOldFiles: DeficiencyFileEdited[] = JSON.parse(JSON.stringify(deficiencyFiles));
    if (sInd !== dInd) {
      const flag = Object.keys(showOnReportImages).length !== 0 ? 'fileId' : 'updatedName';
      const items = move(defFilePayloadData, draggableIdarray[0], sInd, dInd, flag);
      defFilePayloadData = items;
      Object.keys(defFilePayloadData).map(key => {
        defFilePayloadData[key].map((checkedItem: DeficiencyFileEdited, index: number) => {
          const changedItem = checkedItem;
          changedItem.fileSortOrder = index;
        });
      });
      if (Object.keys(showOnReportImages).length !== 0) setShowOnReportImages(defFilePayloadData);
      else setShowOnReportImages1(defFilePayloadData);
      Object.keys(defFilePayloadData).map(key => {
        defFilePayloadData[key].map(async (item: DeficiencyFileEdited) => {
          const fileIndex = listOfOldFiles.findIndex(
            (oldFile: DeficiencyFileEdited) => oldFile.fileAimsUUID === item.fileAimsUUID,
          );
          listOfOldFiles[fileIndex].fileName = item.fileName;
          listOfOldFiles[fileIndex].fileDescription = item.fileDescription;
          listOfOldFiles[fileIndex].showOnReport = item.showOnReport;
          listOfOldFiles[fileIndex].fileSortOrder = item.fileSortOrder;
          listOfOldFiles[fileIndex].operation = FileOperation.fileDetailsEdited;
        });
      });
      setDeficiencyFiles(listOfOldFiles);
      if (isDeficiencyEditable) {
        validateInputs();
      }
    } else {
      const reorderResult = reorder(defFilePayloadData[dInd], source.index, destination.index);
      defFilePayloadData[sInd] = reorderResult;
      defFilePayloadData[sInd].map((item: DeficiencyFileEdited, index: number) => {
        defFilePayloadData[sInd][index].fileSortOrder = index;
      });
      if (Object.keys(showOnReportImages).length !== 0) setShowOnReportImages(defFilePayloadData);
      else setShowOnReportImages1(defFilePayloadData);
      if (isDeficiencyEditable) {
        validateInputs();
      }
      defFilePayloadData[sInd].map((item: DeficiencyFileEdited, index: number) => {
        const fileIndex = listOfOldFiles.findIndex(
          (oldFile: DeficiencyFileEdited) => oldFile.fileAimsUUID === item.fileAimsUUID,
        );
        listOfOldFiles[fileIndex].fileName = item.fileName;
        listOfOldFiles[fileIndex].fileDescription = item.fileDescription;
        listOfOldFiles[fileIndex].showOnReport = item.showOnReport;
        listOfOldFiles[fileIndex].fileSortOrder = index;
        listOfOldFiles[fileIndex].operation = FileOperation.fileDetailsEdited;
      });
      setDeficiencyFiles(listOfOldFiles);
    }
    if (wasCreatedInPreviousInspection) {
      updateDeficiencyToReinspected();
    }
  };

  const clearData = () => {
    setFilesToUpload([]);
    setDeficiencyActionedList({} as DeficiencyActionedList);
    setDeficiencyComponentRemovalPayload({} as DeficiencyComponentRemovalPayload);
    onClose();
    setMaxEnvironmentalCondition(0);
  };

  const handleCancelConfirmation = () => {
    if (isChange || deficiencyPayload.isChanged) {
      setOpenCancelConfirmation(!openCancelConfirmation);
    } else {
      clearData();
    }
  };

  const showUpdateButton = () => {
    const taskStatus = CurrentUserSelected.isAdhoc
      ? [TaskStatus.LI_PENDING_APPROVAL]
      : [TaskStatus.LI_PENDING_APPROVAL, TaskStatus.LI_SIGN_PENDING];
    const inspectionLevelStatus = CurrentUserSelected.isAdhoc
      ? [InspectionStatus.LI_PENDING_APPROVAL]
      : [InspectionStatus.LI_PENDING_APPROVAL, InspectionStatus.LI_SIGN_PENDING];
    return (
      (taskStatus.includes(taskData?.fkInspectionTaskStatusCategory) &&
        inspectionLevelStatus.includes(inspection?.assetInspectionStatusId) &&
        currentUser?.inspectorId === taskData?.fkInspectorLead &&
        taskData?.fkInspectorField !== taskData?.fkInspectorLead) ||
      isEditable
    );
  };

  const updateFileList = (updatedFileList: DeficiencyFileEdited[]) => {
    setDeficiencyFiles(updatedFileList);
    if (isActiveDeficiency) {
      updateDeficiencyToReinspected();
    }
  };

  const updateUploadFileList = (updatedFileList: AimsFile[]) => {
    setFilesToUpload(updatedFileList);
  };

  const onChangeFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    let checkIFFile = false;
    let validItem = null;
    if (files !== null) {
      for (let i = 0; i < files.length; i++) {
        if (hasSpecialCharacter(files[i].name)) {
          errorNotification(SPECIAL_CHARACTER_ERROR);
          return;
        }
        validItem = await checkFileCorruption([files[i]]);
        if (!validItem.length) {
          errorNotification(INVALID_FILE);
          return;
        }
      }
      const listOfFiles: AimsFile[] = Array.from(files as unknown as AimsFile[]);
      listOfFiles.forEach((file: AimsFile, index: number) => {
        if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
          alert('Only images are allowed');
          checkIFFile = true;
          return;
        }
        listOfFiles[index].updatedName = autoVersionIfFileExists(file.name, selectedFiles);
        listOfFiles[index].fileDescription = '';
        listOfFiles[index].showOnReport = false;
      });
      if (!checkIFFile) {
        setSelectedFiles([...selectedFiles, ...listOfFiles]);
        setOpenFileUploadView(true);
      }
    }
  };

  const onOpenFileUpload = () => {
    document.getElementById('selectImageForEditDef')?.click();
  };

  const handleFileUploadViewClose = () => {
    setSelectedFiles([]);
    setOpenFileUploadView(false);
  };

  const checkShowOnReportCount = () => {
    const filesToUploadGrouped = groupObjectArrayByKey(filesToUpload, 'showOnReport');
    const deficiencyFilesGrouped = groupObjectArrayByKey(deficiencyFiles, 'showOnReport');
    const selectedFilesGrouped = groupObjectArrayByKey(selectedFiles, 'showOnReport');
    let countOfShowOnReportFiles = 0;
    if (filesToUploadGrouped?.true?.length) {
      countOfShowOnReportFiles += filesToUploadGrouped?.true?.length;
    }
    if (deficiencyFilesGrouped?.true?.length) {
      countOfShowOnReportFiles += deficiencyFilesGrouped?.true?.length;
    }
    if (selectedFilesGrouped?.true?.length) {
      countOfShowOnReportFiles += selectedFilesGrouped?.true?.length;
    }
    return countOfShowOnReportFiles >= 5;
  };

  useEffect(() => {
    if (filesToUpload) {
      const imagesToUploadEditedFiles = groupObjectArrayByKey(filesToUpload, 'showOnReport');
      if (imagesToUploadEditedFiles) {
        imagesToUploadEditedFiles.false = imagesToUploadEditedFiles.false
          ? imagesToUploadEditedFiles.false
          : [];

        // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-explicit-any
        imagesToUploadEditedFiles.false?.forEach((item: any, index: number) => (item.id = index));
      }
      if (
        imagesToUploadEditedFiles.true !== undefined &&
        imagesToUploadEditedFiles.true.length > 5
      ) {
        imagesToUploadEditedFiles.false = imagesToUploadEditedFiles.false.concat(
          imagesToUploadEditedFiles.true.slice(5),
        );
        imagesToUploadEditedFiles?.false.filter(
          (item: AimsFile, index: number) =>
            (imagesToUploadEditedFiles.false[index].showOnReport =
              item.showOnReport === true ? false : item.showOnReport),
        );
        imagesToUploadEditedFiles.true = imagesToUploadEditedFiles.true.slice(0, 5);
      }
      setShowOnReportImages1(imagesToUploadEditedFiles);
    }
  }, [filesToUpload]);

  useEffect(() => {
    if (editFile?.name) {
      setDeficiencyPayload(JSON.parse(JSON.stringify({ ...editFile, isChanged: false })));
      setDeficiencyFiles([...deficiencyFiles, ...editFile?.deficiencyFiles]);
      setDeficiencyActionedList(editFile?.deficiencyActionedList);
      setDeficiencyComponentRemovalPayload({
        ...editFile?.deficiencyComponentRemoval,
        isChanged: false,
      });
    }
  }, [editFile]);

  useEffect(() => {
    let subscription: Subscription;
    if (inspectionListInitialized && inspectionListCollection) {
      const queryObj = {
        selector: { inspectionId: { $eq: String(CurrentUserSelected.inspectionId) } },
      };
      subscription = inspectionListCollection
        .find(queryObj)
        .$.subscribe(getInspectionListDataFromRxdb);
    }
    return () => {
      subscription?.unsubscribe();
    };
  }, [inspectionListInitialized]);

  useEffect(() => {
    setAreasList(() => treeData);
  }, [treeData]);

  useEffect(() => {
    if (
      deficiencyActionedList &&
      Object.keys(deficiencyActionedList).length &&
      ((deficiencyComponentRemovalPayload.componentRemovalId &&
        deficiencyComponentRemovalPayload.isChanged) ||
        filesToUpload?.length)
    ) {
      if (!deficiencyPayload.isReInspectedComponent) {
        setDeficiencyPayload({
          ...deficiencyPayload,
          isReInspectedComponent: wasCreatedInPreviousInspection && !!deficiencyActionedList,
          isChanged: true,
        });
      }
    }
  }, [deficiencyComponentRemovalPayload, filesToUpload]);

  useEffect(() => {
    const setupTreeData = () => {
      const areaList = inspection?.areaList;
      if (areaList?.length) {
        getTreeData(JSON.parse(JSON.stringify(assetsByAreaHierarchyData))[0]?.children, areaList);
        getTreeData(JSON.parse(JSON.stringify(assetsByAreaHierarchyData))[1]?.children, areaList);
        getTreeData(JSON.parse(JSON.stringify(assetsByAreaHierarchyData))[2]?.children, areaList);
      }
    };
    if (assetsByAreaHierarchyData?.length && inspection) {
      setupTreeData();
    }
  }, [inspection, assetsByAreaHierarchyData]);

  useEffect(() => {
    const defFilePayloadImages = groupObjectArrayByKey(
      deficiencyFiles.filter(item => item.isDelete === 'False'),
      'showOnReport',
    );
    setShowOnReportImages(defFilePayloadImages);
    const deficiencyFilesEdited = deficiencyFiles as DeficiencyFileEdited[];
    const isFileUpdate = deficiencyFilesEdited.every((item: DeficiencyFileEdited) => {
      if (item?.operation && item?.operation !== FileOperation.noChange) {
        return false;
      } else {
        return true;
      }
    });
    if ((!isFileUpdate || filesToUpload.length > 0) && valid) {
      setValid(false);
    } else {
      setValid(true);
    }
    if (isElectrical) {
      validateInputForElectrical();
    } else {
      validateInputs();
    }
  }, [deficiencyFiles, filesToUpload]);

  useEffect(() => {
    const imagesToUploadEditedFiles = groupObjectArrayByKey(filesToUpload, 'showOnReport');
    if (imagesToUploadEditedFiles) {
      imagesToUploadEditedFiles.false = imagesToUploadEditedFiles.false
        ? imagesToUploadEditedFiles.false
        : [];
    }
    if (imagesToUploadEditedFiles.true !== undefined && imagesToUploadEditedFiles.true.length > 5) {
      imagesToUploadEditedFiles.false = imagesToUploadEditedFiles.false.concat(
        imagesToUploadEditedFiles.true.slice(5),
      );
      imagesToUploadEditedFiles.false.map((item: AimsFile, index: number) => {
        imagesToUploadEditedFiles.false[index].showOnReport = !item.showOnReport;
      });
      imagesToUploadEditedFiles.true = imagesToUploadEditedFiles.true.slice(0, 5);
    }
    setShowOnReportImages1(imagesToUploadEditedFiles);
  }, [selectedFiles]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !valid) {
      handleSave();
    }
  };

  const updateValidComment = (flag: boolean) => {
    setValidComment(flag);
  };

  return (
    <>
      <Dialog
        data-testid="add-edit-def-dialog"
        disableEnforceFocus
        onClose={handleCancelConfirmation}
        fullScreen
        open={open}
        titleText={addFlag ? 'Add Deficiency' : 'Edit Deficiency'}
        backButton={false}
        dialogActions={
          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            className={classes.dialogActionGridContainer}
          >
            {showUpdateButton() && (
              <>
                <Grid item className={classes.dialogActionGrid}>
                  <Link
                    data-testid="cancel-add-edit-def"
                    to=""
                    onClick={handleCancelConfirmation}
                    color="primary"
                    className={classes.dialogActionGridLink}
                  >
                    Cancel
                  </Link>
                </Grid>
                <Grid item>
                  <Button
                    data-testid="edit-deficiency-update"
                    id="edit-deficiency-update"
                    disabled={valid || validCommment}
                    noMinHeight
                    color="primary"
                    variant="contained"
                    className="dialogActionSaveButton"
                    type="submit"
                    onClick={() => !updating && handleSave()}
                    isLoading={updating}
                  >
                    <Typography fontWeight={700} className={classes.dialogActionSaveButtonLabel}>
                      {addFlag ? 'Save' : 'Update'}
                    </Typography>
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        }
        maxWidth="lg"
      >
        {isActiveDeficiency && (
          <Grid container item xs={12} justifyContent="flex-end" alignItems="right">
            <Typography
              color="primary"
              fontWeight={600}
              onClick={() => setOpenAuditLogsDialog(true)}
              className={classes.updateHistory}
            >
              <u>Update History</u>
            </Typography>
          </Grid>
        )}
        <Grid className={`${classes.card} ${classes.photosDialogGrid}`} xs={12} container item>
          <Grid item xs={6}>
            <>
              <Typography variant="h5" className={classes.editDefPhotosLabel}>
                Photos
                {showToolTip && (
                  <Tooltip
                    PopperProps={{ disablePortal: true }}
                    onClose={e => (openForAlertInfo ? handleTooltipCloseForAlertInfo(e) : '')}
                    open={openForAlertInfo}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    title={
                      <Typography className="tooltipTextColor">
                        {filesNotAvailableToolTip}
                      </Typography>
                    }
                    className={classes.inlineBlock}
                    placement="right"
                    arrow
                  >
                    <Typography
                      data-testid="asgn-back-tooltip"
                      onClick={e => {
                        if (openForAlertInfo) handleTooltipCloseForAlertInfo(e);
                        else handleTooltipOpenForAlertInfo(e);
                      }}
                    >
                      <Icons.AlertCircleIcon color="error" aria-label="View" />
                    </Typography>
                  </Tooltip>
                )}
              </Typography>
            </>
          </Grid>

          <Grid item xs={6} container justifyContent="flex-end" alignItems="center">
            {isDeficiencyEditable && (
              <Grid item>
                <Button
                  noMinHeight
                  color="primary"
                  variant="contained"
                  onClick={onOpenFileUpload}
                  data-testid="add-deficiency-photos"
                >
                  <Typography className={classes.addDeficiencyButtonIcon}>
                    <Icons.PlusFeather />
                  </Typography>
                  <Typography fontWeight={700}>Add...</Typography>
                </Button>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg"
                  onChange={evt => onChangeFilesSelected(evt)}
                  onClick={(event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
                    const element = event.target as HTMLInputElement;
                    element.value = '';
                  }}
                  hidden
                  multiple
                  id="selectImageForEditDef"
                  data-testid="add-deficiency-files"
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid className={classes.editDefImagesCarouselGrid}>
          <Grid container className={classes.editDefImagesCarouselContainer}>
            <div id="markerOverlay" />

            {deficiencyFiles?.length > 0 || filesToUpload?.length > 0 ? (
              <DragDropContext onDragEnd={onDragEnd}>
                <DeficiencyFilesView
                  showOnReportImages={showOnReportImages}
                  showOnReportImages1={showOnReportImages1}
                  fileList={deficiencyFiles}
                  filesToUpload={filesToUpload}
                  updateFileList={updateFileList}
                  updateUploadFileList={updateUploadFileList}
                  isEditable={isDeficiencyEditable}
                  showInfoMessage={showInfoMessage}
                  isActiveDeficiency={isActiveDeficiency}
                />
              </DragDropContext>
            ) : (
              <Typography className={classes.defNoPhotosLabel}>
                {!isDeficiencyEditable
                  ? 'No file added for this deficiency.'
                  : 'No photos added yet. Click the add button to add photos.'}
              </Typography>
            )}
          </Grid>
        </Grid>

        {isElectrical ? (
          <ComponentInfoPanelForElectrical
            deficiencyPayloadObj={deficiencyPayload}
            remedialActionListData={remedialActionListData}
            selectComponentLocations={selectComponentLocations}
            validateInputs={validateInputForElectrical}
            findEnvironmentalCondition={findEnvironmentalCondition}
            handleKeyDown={handleKeyDown}
            addFlag={addFlag}
            isEditable={isDeficiencyEditable}
          />
        ) : (
          <ComponentInfoPanel
            deficiencyPayloadObj={deficiencyPayload}
            componentTypeListData={componentTypeListData}
            remedialActionListData={remedialActionListData}
            primaryCauseOfFailureListData={primaryCauseOfFailureListData}
            selectComponentLocations={selectComponentLocations}
            validateInputs={validateInputs}
            findTrafficSafetyScore={findTrafficSafetyScore}
            isShaft={isShaft}
            elevationRange={elevationRange}
            setDeficiencyPayload={setDeficiencyPayload}
            handleKeyDown={handleKeyDown}
            isEditable={isDeficiencyEditable}
          />
        )}

        {isElectrical ? (
          <RiskAssessmentPanelForElectrical
            repairFactorListData={repairFactorListData}
            deficiencyPayload={deficiencyPayload}
            validateInputs={validateInputForElectrical}
            environmentalConditionListData={environmentalConditionListData}
            maxEnvironmentalCondition={maxEnvironmentalCondition}
            componentAreaLength={componentAreaLength}
            setDeficiencyPayload={setDeficiencyPayload}
            isEditable={isDeficiencyEditable}
          />
        ) : (
          <RiskAssessmentPanel
            conditionRatingListData={conditionRatingListData}
            urgencyListData={urgencyListData}
            operationalDowntimeListData={operationalDowntimeListData}
            trafficSafetyScoreListData={trafficSafetyScoreListData}
            validateInputs={validateInputs}
            trafficSafetyId={trafficSafetyId}
            setValid={setValid}
            componentAreaLength={componentAreaLength}
            setIsChange={setIsChange}
            isEditable={isDeficiencyEditable}
            setDeficiencyPayload={setDeficiencyPayload}
            deficiencyPayload={deficiencyPayload}
          />
        )}

        {isActiveDeficiency && (
          <ComponentRemovalPanel
            deficiencyPayloadObj={deficiencyPayload}
            ComponentRemovalListData={componentRemovalListData}
            deficiencyComponentRemovalPayload={deficiencyComponentRemovalPayload}
            validateInputs={isElectrical ? validateInputForElectrical : validateInputs}
            isEditable={isEditable}
            setDeficiencyComponentRemovalPayload={setDeficiencyComponentRemovalPayload}
            setDeficiencyPayload={setDeficiencyPayload}
          />
        )}
        {deficiencyActionedList && Object.keys(deficiencyActionedList).length > 0 && (
          <DeficiencyActionPanel
            deficiencyActionedList={deficiencyActionedList}
            isEditable={false}
          />
        )}

        {editFile &&
          Object.keys(editFile).length > 0 &&
          editFile?.componentFailureId !== '0' &&
          inspection &&
          taskData && (
            <DeficiencyComments
              deficiencyPayloadObj={deficiencyPayload}
              originalDeficiencyPayload={editFile}
              validateInputs={validateComments}
              updateValidComment={updateValidComment}
              inspection={inspection}
              taskData={taskData}
              isEditable={isDeficiencyEditable}
              isActiveDeficiency={isActiveDeficiency}
              setDeficiencyPayload={setDeficiencyPayload}
            />
          )}
      </Dialog>

      {openFileUploadView && (
        <FileSelectionView
          selectedFilesProps={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          setFilesToUpload={setFilesToUpload}
          filesToUpload={filesToUpload}
          onOpenFileUpload={onOpenFileUpload}
          openFileUploadView={openFileUploadView}
          handleFileUploadViewClose={handleFileUploadViewClose}
          checkShowOnReportCount={checkShowOnReportCount}
          isDirectFileUpload={false}
          handleDirectFileUpload={() => {}}
          isLoading={false}
          isEditable={isDeficiencyEditable}
          setIsChange={setIsChange}
        />
      )}

      {openSidePanel && (
        <GenericSidePanel
          open={openSidePanel}
          treeData={[...new Map(areasList.map((item: HeirachyResult) => [item.id, item])).values()]}
          searchText={searchText}
          handleTreeDataSearch={handleTreeDataSearch}
          onClose={() => {
            setSearchText('');
            setAreasList(treeData);
            setOpenSidePanel(false);
          }}
          defaultSelected={deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]}
          onSave={(selectedFromPanel: [ComponentAreaInfo]) => {
            setDeficiencyPayload(prev => {
              const clone = { ...prev };
              clone[ComponentInfo.COMPONENT_AREA_INFO] = selectedFromPanel;
              clone.isChanged = true;
              return clone;
            });
            setOpenSidePanel(false);
            if (isElectrical) {
              findEnvironmentalCondition();
            } else {
              findTrafficSafetyScore();
            }
          }}
          title="Component Location"
          componentVersionId={componentVersionId}
        />
      )}

      {openCancelConfirmation && (
        <CancelConfirmation
          open={openCancelConfirmation}
          onClose={(value: boolean) => {
            if (value) {
              clearData();
            }
            handleCancelConfirmation();
          }}
        />
      )}
      {openAuditLogsDialog && (
        <AuditLogs
          open={openAuditLogsDialog}
          onClose={() => {
            setOpenAuditLogsDialog(false);
          }}
          title="Deficiency"
          subTitle="Inspection"
          deficiencyId={Number(editFile?.componentFailureId)}
          inspectionIdForActiveDeficiency={Number(editFile?.assetInspectionId)}
          isUpdateHistory
          isActiveDeficiency={false}
        />
      )}
    </>
  );
};

export default EditDeficiency;
